// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { API_BASE } from '@/config';
import type { ShortFileProp, PresignedUrlProp } from '@/types/s3-file';
import type { File as S3File } from '@/models';
export const MAX_FILE_SIZE_S3_ENDPOINT = 100;
export const FILE_NUMBER_LIMIT = 10;

/**
 *
 * @param files array of files
 * @param maxSizeMB
 * @returns true if all files are valid
 */
export function validateFiles(
  files: ShortFileProp[],
  maxSizeMB: number
): {
  success: boolean;
  message?: string;
} {
  const totalFileSize = files.reduce((acc, file) => acc + file.fileSize, 0);
  const isFileSizeValid = totalFileSize < maxSizeMB * 1024 * 1024;
  if (!isFileSizeValid) {
    return {
      message: `Total file size should be less than ${maxSizeMB} MB`,
      success: false,
    };
  }
  if (files.length > FILE_NUMBER_LIMIT) {
    return {
      message: `You can upload maximum ${FILE_NUMBER_LIMIT} files at a time`,
      success: false,
    };
  }
  return { success: true };
}

/**
 * @deprecated
 * Gets presigned urls for uploading files to S3
 * @returns
 * @param files
 */
export const getPresignedUrls = async (files: ShortFileProp[]) => {
  const response = await fetch(`${API_BASE}/api/files/upload/presignedUrl`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(files),
  });
  return (await response.json()) as PresignedUrlProp[];
};

/**
 * NOT TESTED, USE AT YOUR OWN RISK
 * Gets presigned urls for uploading files to S3
 * @returns
 * @param files
 * @param folderName
 */
export const getPresignedUrlsWithFolder = async (
  files: ShortFileProp[],
  folderName: string
) => {
  const response = await fetch(
    `${API_BASE}/api/files/upload/presignedUrl/${encodeURIComponent(folderName)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(files),
    }
  );
  return (await response.json()) as PresignedUrlProp[];
};

/**
 * Uploads file to S3 directly using presigned url
 * @param presignedUrl presigned url for uploading
 * @param file  file to upload
 * @param onProgress
 * @returns  response from S3
 */
export const uploadToS3 = async (
  presignedUrl: PresignedUrlProp,
  file: File,
  onProgress: (progressEvent: ProgressEvent) => void
) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl.url);

    xhr.upload.onprogress = onProgress;

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve({ status: xhr.status });
      } else {
        reject(new Error(`Failed to upload file: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Error during file upload'));
    };

    xhr.setRequestHeader('Content-Type', file.type);
    xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
    xhr.send(file);
  });
};

/**
 * Saves file info in DB
 * @param presignedUrls presigned urls for uploading
 * @returns
 */
export const saveFileInfoInDB = async (presignedUrls: PresignedUrlProp[]) => {
  return await fetch(`${API_BASE}/api/files/upload/saveFileInfo`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(presignedUrls),
  });
};

interface UploadFileProps {
  files: File[];
  presignedUrls: PresignedUrlProp[];
  onProgress: (fileIndex: number, progressEvent: ProgressEvent) => void;
  onUploadSuccess: (savedFile: S3File[]) => void;
}

/**
 * Uploads files to S3 and saves file info in DB
 * @param files files to upload
 * @param presignedUrls  presigned urls for uploading
 * @param onProgress - returns progressEvent for fileIndex
 * @param onUploadSuccess callback to execute after successful upload, returns savedFile.json() from db
 * @returns
 */
export const handleUpload = async ({
  files,
  presignedUrls,
  onProgress,
  onUploadSuccess,
}: UploadFileProps) => {
  await Promise.all(
    presignedUrls.map((presignedUrl, index) => {
      const file = files.find(
        (file) =>
          file.name === presignedUrl.originalFileName &&
          file.size === presignedUrl.fileSize
      );
      if (!file) {
        throw new Error('File not found');
      }
      return uploadToS3(presignedUrl, file, (progressEvent) =>
        onProgress(index, progressEvent)
      );
    })
  );

  const savedFile = await saveFileInfoInDB(presignedUrls);
  const savedFileFromDb = (await savedFile.json()) as S3File[];

  onUploadSuccess(savedFileFromDb);

  return savedFileFromDb;
};

/**
 *
 * @param bytes size of file
 * @param decimals number of decimals to show
 * @returns formatted string
 */
export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB'];

  const index = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, index)).toFixed(dm)) + ' ' + sizes[index]
  );
}

/**
 * @deprecated
 * @param files array of files
 * @returns FormData object
 */
export function createFormData(files: File[]): FormData {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('file', file);
  });
  return formData;
}
